import { useCallback, useEffect, useRef, useState } from "react";

const GOFTINO_CONTAINER_ID = "goftino_w";
const GOFTINO_MOBILE_MAX_WIDTH = 768;

export type GoftinoWidgetParams = {
  iconUrl?: string;
  cssUrl?: string;
  soundUrl?: string;
  marginRight?: number;
  marginLeft?: number;
  marginBottom?: number;
  hasIcon?: boolean;
  hasSound?: boolean;
  counter?: string;
};

const closeGoftinoPanel = () => {
  try {
    window.Goftino?.close();
  } catch (error) {
    console.log(error);
  }
};

/** `false` = force hide; `true` = force show; `undefined` = show except on small viewports. */
const applyGoftinoWidgetVisibility = (showIcon: boolean | undefined) => {
  const target = document.getElementById(GOFTINO_CONTAINER_ID);
  if (!target) return;

  const shouldHide =
    showIcon === false ||
    (showIcon === undefined && window.innerWidth <= GOFTINO_MOBILE_MAX_WIDTH);

  target.classList.toggle("hidden", shouldHide);
};

export const useGoftino = () => {
  const [showGoftinoIcon, setShowGoftinoIcon] = useState<boolean | undefined>();
  const showGoftinoIconRef = useRef(showGoftinoIcon);
  showGoftinoIconRef.current = showGoftinoIcon;

  useEffect(() => {
    const sync = () => applyGoftinoWidgetVisibility(showGoftinoIcon);

    sync();
    window.addEventListener("goftino_ready", sync);
    window.addEventListener("resize", sync);

    return () => {
      window.removeEventListener("goftino_ready", sync);
      window.removeEventListener("resize", sync);
    };
  }, [showGoftinoIcon]);

  const setWidget = useCallback((params: GoftinoWidgetParams) => {
    const run = () => {
      applyGoftinoWidgetVisibility(showGoftinoIconRef.current);
      window.Goftino?.setWidget(params);
      closeGoftinoPanel();
    };

    if (window.Goftino) {
      run();
    } else {
      window.addEventListener("goftino_ready", run, { once: true });
    }
  }, []);

  const showGoftino = useCallback(() => {
    setShowGoftinoIcon(true);
  }, []);

  const hideGoftino = useCallback(() => {
    setShowGoftinoIcon(false);
  }, []);

  const initialGoftino = useCallback((isFrdsChat: boolean) => {
    setShowGoftinoIcon(isFrdsChat ? false : undefined);
  }, []);

  return {
    showGoftino,
    hideGoftino,
    initialGoftino,
    setWidget,
  };
};
